/**
 * メール配信の画面（pages/mail/）の「判断」だけを集めた純関数（React・i18n・fetch に依存しない）。
 *
 * WHY 分けるか: メールは送ったら取り消せない（相手の受信箱に届く・1 通ごとに原価がかかる）。
 *   「送ってよい状態か」「入力した時刻が UTC で何時になるか」「入力に誤りは無いか」は画面の描画と切り離して
 *   `node --test` で確かめたい（mailLogic.test.ts）。moneyLogic.ts と同じく import を持たない
 *   = Node の型の除去（--experimental-strip-types）だけでテストが動く。
 *
 * 文言はここに書かない。返すのは状態の名前（'sender_incomplete' 等）で、画面が i18n のキーに引き直す。
 *
 * API の形は Core の確定した実装（docs/teams/core/mail-broadcast-api.md の末尾「実装で決めた細部」・2026-09-27）。
 */

// ---------------------------------------------------------------- 上限

/**
 * 入力の上限（画面側の目安）。サーバーの検証が正で、ここは「送ってから 400 で戻される」を減らすためだけのもの。
 * 件名 200 = メールソフトの一覧で読める長さを大きく超える量（長い件名は迷惑メールの判定でも不利）。
 * 本文 100,000 = サーバー（mailBroadcast/broadcasts.ts の createBody）と同じ値。以前は画面だけ 50,000 で、サーバーが受ける長さを
 *   画面が断っていた（2026-10-04 console/limits-ui・洗い出し L093 で合わせた）。サーバーの値を変えたらここも同じ変更で直す
 *   （一斉メールの上限を返す API はまだ無い = 写しはこの 1 か所だけにする）。
 * 住所 500 = 私書箱・建物名・国名まで入れても足りる。氏名 / 法人名 200・メール 254（RFC 5321 の上限）
 */
export const MAIL_LIMITS = {
  legalName: 200,
  postalAddress: 500,
  contactEmail: 254,
  subject: 200,
  text: 100_000
} as const;

/**
 * 予約の時刻は今から何分より後か。5 分 = 確認のダイアログを読んでいる間に「過去」にならない余裕
 * （予約は Cron が分単位で拾う。過去の時刻を送ると、サーバーが即時に送るか断るかは実装しだい = どちらも意図と違う）
 */
export const MIN_LEAD_MINUTES = 5;
/** 予約は 1 年先まで。打ち間違い（2062 年など）で「いつまでも送られない予約」が残るのを防ぐ */
export const MAX_AHEAD_DAYS = 365;

// ---------------------------------------------------------------- 応答の包み

/**
 * `{ sender: X }` / `{ broadcast: X }` / `{ broadcasts: X }` の中身を取り出す。包みが無い・中身が null なら null（= まだ無い。例: 差出人を一度も保存していない）。
 * WHY 包みの無い形を受けないか（2026-09-27 mail-align で狭めた）: 案の段階では包み方が決まっておらず「どちらでも読む」にしていたが、
 *   Core の実装は必ず包む。包みの無い応答を中身として読むと、別の形の応答（エラーの本文・古いサーバー）を差出人や配信として
 *   黙って表示してしまう = 「無い」と扱う方が安全（画面は「未入力」「履歴なし」を出す）。
 *   状態（GET …/mail/status）だけは包まずに返る = unwrap を通さずにそのまま読む（MailPage.tsx）
 */
export function unwrap<T>(res: unknown, key: string): T | null {
  if (res == null || typeof res !== 'object') return null;
  const r = res as Record<string, unknown>;
  return key in r ? ((r[key] ?? null) as T | null) : null;
}

/** 宛先の数の応答 `{ count }`（Core の確定した形）。数が無ければ null（= 不明。確認のダイアログに「不明」と出す） */
export function audienceCount(res: unknown): number | null {
  if (res == null || typeof res !== 'object') return null;
  const c = (res as Record<string, unknown>).count;
  return typeof c === 'number' ? c : null;
}

// ---------------------------------------------------------------- 差出人情報（開発者単位）

export interface MailSenderInput {
  legalName: string;
  postalAddress: string;
  contactEmail: string;
}
export type SenderField = keyof MailSenderInput;
const SENDER_FIELDS: SenderField[] = ['legalName', 'postalAddress', 'contactEmail'];

/**
 * 差出人情報で空の欄。1 つでも空なら送れない（サーバーは 400 sender_incomplete）。
 * WHY 送る前に画面で止めるか: フッターに正式な氏名 / 法人名・住所・問い合わせ先を入れるのは法律の義務
 *   （米国 CAN-SPAM の物理住所・日本の特定電子メール法の表示義務）。空のまま「送る」を押させて 400 で戻すより、先に埋めてもらう
 */
export function senderMissing(s: Partial<MailSenderInput> | null | undefined): SenderField[] {
  return SENDER_FIELDS.filter((f) => !(s?.[f] ?? '').trim());
}

export type SenderProblem = 'required' | 'tooLong' | 'email';

/** RFC を厳密に追わない形の確認（@ の前後があり、後ろに . がある・空白なし）。届くかは送ってみないと分からないので、明らかな打ち間違いだけ止める */
export function looksLikeEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

export function validateSender(s: MailSenderInput): Array<{ field: SenderField; problem: SenderProblem }> {
  const out: Array<{ field: SenderField; problem: SenderProblem }> = [];
  for (const f of SENDER_FIELDS) {
    const v = s[f].trim();
    if (!v) out.push({ field: f, problem: 'required' });
    else if (v.length > MAIL_LIMITS[f]) out.push({ field: f, problem: 'tooLong' });
    else if (f === 'contactEmail' && !looksLikeEmail(v)) out.push({ field: f, problem: 'email' });
  }
  return out;
}

// ---------------------------------------------------------------- 送れる状態か

/**
 * 送る操作を出せない理由（重い順）。空 = 送れる。
 *   kids              … 年齢層が kids のゲーム。メールは大人のプレイヤーだけ（D1）なので、そもそも ON にできない
 *   feature_off       … features.mail が OFF（既定 OFF）
 *   suspended         … 苦情・宛先不明が多く自動で止まっている（解除は運営の確認）
 *   sender_incomplete … 差出人情報が揃っていない
 * featureEnabled が null / undefined = サーバーの機能一覧に mail がまだ無い（古いサーバー）→ ここでは止めない（API に任せる）
 */
export type MailBlock = 'kids' | 'feature_off' | 'suspended' | 'sender_incomplete';

export function mailBlocks(input: {
  audience: string | null | undefined;
  featureEnabled: boolean | null | undefined;
  state: 'active' | 'suspended' | null | undefined;
  senderMissing: number;
}): MailBlock[] {
  const out: MailBlock[] = [];
  if (input.audience === 'kids') out.push('kids');
  if (input.featureEnabled === false) out.push('feature_off');
  if (input.state === 'suspended') out.push('suspended');
  if (input.senderMissing > 0) out.push('sender_incomplete');
  return out;
}

// ---------------------------------------------------------------- 時刻（入力はブラウザの現地時刻・送るのは UTC）

/**
 * ブラウザの現地時刻の「日付 + 時刻」（<input type=date> の 'YYYY-MM-DD' と <input type=time> の 'HH:MM'）を UTC の ISO に直す。
 * 読めない・存在しない時刻（夏時間に切り替わる瞬間の 2:30 など）は null。
 * WHY 現地時刻で受けるか: クリエイターは「夜 8 時に送りたい」と自分の時計で考える。UTC を打たせると 9 時間ずれた予約が必ず起きる。
 *   API は UTC で受ける（Core の確定した形）ので、変換はこの 1 か所だけで行う
 */
export function localToUtcIso(date: string, time: string): string | null {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const tm = /^(\d{2}):(\d{2})$/.exec(time);
  if (!dm || !tm) return null;
  const [y, mo, d, h, mi] = [Number(dm[1]), Number(dm[2]), Number(dm[3]), Number(tm[1]), Number(tm[2])];
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return null;
  const local = new Date(y, mo - 1, d, h, mi, 0, 0);
  // Date は 2/31 を 3/3 に、夏時間の無い 2:30 を 3:30 に黙って直す。直された = その時刻は存在しない → 受けない
  if (local.getFullYear() !== y || local.getMonth() !== mo - 1 || local.getDate() !== d || local.getHours() !== h || local.getMinutes() !== mi) return null;
  return local.toISOString();
}

/** UTC の 'HH:MM'（定期の recurrence.at に入れる形） */
export function utcHm(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

/**
 * UTC の 'HH:MM' を、ブラウザの現地時刻の 'HH:MM' にして見せる（履歴の「毎日 20:00」など）。
 * ref の日付の UTC で組み立てる = 夏時間のある地域では日付によって 1 時間ずれうる（定期は UTC 固定なので、それが実際の挙動）
 */
export function utcHmToLocal(at: string, ref: Date = new Date()): string {
  const m = /^(\d{2}):(\d{2})$/.exec(at);
  if (!m) return at;
  const d = new Date(Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), ref.getUTCDate(), Number(m[1]), Number(m[2])));
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * ブラウザのタイムゾーンに夏時間があるか（1 月と 7 月で UTC との差が違う）。
 * WHY: 定期は UTC の時刻で固定する（Core の確定した形）ので、夏時間のある地域では季節で現地の時刻が 1 時間ずれる。
 *   その注意は夏時間のある人にだけ出す（日本のクリエイターには関係の無い注意で画面を埋めない）
 */
export function observesDst(year: number): boolean {
  return new Date(year, 0, 1).getTimezoneOffset() !== new Date(year, 6, 1).getTimezoneOffset();
}

/** 予約の入力欄の初期値（空にしない）: 今の現地時刻の 1 時間後の 0 分（<input type=date/time> の形） */
export function defaultLocalSlot(now: Date): { date: string; time: string } {
  const d = new Date(now.getTime() + 60 * 60 * 1000);
  d.setMinutes(0, 0, 0);
  const p = (n: number) => String(n).padStart(2, '0');
  return { date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`, time: `${p(d.getHours())}:00` };
}

// ---------------------------------------------------------------- 本文と送り方の検証

export type SendMode = 'now' | 'schedule' | 'recurring';
export type Every = 'day' | 'week' | 'month';

export interface ComposeForm {
  subject: string;
  text: string;
  /** '' = すべての言語 */
  language: string;
  /** null = 期間で絞らない */
  lastPlayedWithinDays: number | null;
  mode: SendMode;
  /** 予約・定期の最初の回（現地時刻） */
  date: string;
  time: string;
  every: Every;
}

export interface BroadcastBody {
  subject: string;
  text: string;
  filters?: { language?: string; lastPlayedWithinDays?: number };
  sendAt?: string;
  recurrence?: { every: Every; at: string };
}

export type ComposeField = 'subject' | 'text' | 'when';
export type ComposeProblem = 'required' | 'tooLong' | 'newline' | 'invalid' | 'past' | 'tooFar' | 'monthDay';

/**
 * 入力を API の本文に組み立てる。問題があれば body は無し。
 *
 * 定期（recurring）: API は `recurrence: { every, at: 'HH:MM'(UTC) }` + sendAt 必須（Core の確定した形）。
 *   1 回目は sendAt ちょうど、2 回目からは at の時刻。毎週は sendAt の曜日・毎月は sendAt の日にちで続く（どちらも UTC で数える）。
 *   毎月は sendAt の **UTC の日にち**が 1〜28 日だけ（サーバーはそれ以外を 400 で断る = 29〜31 日が無い月を作らない）。
 *   WHY 現地の日付ではなく UTC で見るか: 以前は現地の日付で 1〜28 を見ていたが、サーバーの判定は UTC。
 *   東京の 1 日 08:00 は UTC で前月の末日になり、画面を通っても 400 で戻される（逆に東京の 29 日 08:00 は UTC の 28 日で通る）。
 *   サーバーと同じ物差しで先に止める（2026-09-27 mail-align）
 */
export function buildBroadcast(form: ComposeForm, now: Date): { body: BroadcastBody | null; issues: Array<{ field: ComposeField; problem: ComposeProblem }> } {
  const issues: Array<{ field: ComposeField; problem: ComposeProblem }> = [];
  const subject = form.subject.trim();
  // 本文の前後の空白は残す（字下げ・署名の前の空行は書き手の意図）。空かどうかだけ trim で見る
  const text = form.text.replace(/\r\n/g, '\n');
  if (!subject) issues.push({ field: 'subject', problem: 'required' });
  else if (subject.length > MAIL_LIMITS.subject) issues.push({ field: 'subject', problem: 'tooLong' });
  // 件名の改行はメールのヘッダーを壊す（ヘッダーの差し込み）。サーバーも弾くはずだが、画面で先に止める
  else if (/[\r\n]/.test(form.subject)) issues.push({ field: 'subject', problem: 'newline' });
  if (!text.trim()) issues.push({ field: 'text', problem: 'required' });
  else if (text.length > MAIL_LIMITS.text) issues.push({ field: 'text', problem: 'tooLong' });

  let sendAt: string | undefined;
  if (form.mode !== 'now') {
    const iso = localToUtcIso(form.date, form.time);
    if (!form.date || !form.time) issues.push({ field: 'when', problem: 'required' });
    else if (!iso) issues.push({ field: 'when', problem: 'invalid' });
    else {
      const t = Date.parse(iso);
      if (t < now.getTime() + MIN_LEAD_MINUTES * 60_000) issues.push({ field: 'when', problem: 'past' });
      else if (t > now.getTime() + MAX_AHEAD_DAYS * 86_400_000) issues.push({ field: 'when', problem: 'tooFar' });
      else if (form.mode === 'recurring' && form.every === 'month' && new Date(iso).getUTCDate() > 28) issues.push({ field: 'when', problem: 'monthDay' });
      else sendAt = iso;
    }
  }
  if (issues.length) return { body: null, issues };

  const body: BroadcastBody = { subject, text };
  const filters = audienceFilters(form);
  if (filters) body.filters = filters;
  if (sendAt) body.sendAt = sendAt;
  if (form.mode === 'recurring' && sendAt) body.recurrence = { every: form.every, at: utcHm(sendAt) };
  return { body, issues };
}

/** 宛先の絞り込み（無ければ undefined = 同意した全員） */
export function audienceFilters(f: Pick<ComposeForm, 'language' | 'lastPlayedWithinDays'>): BroadcastBody['filters'] | undefined {
  const out: NonNullable<BroadcastBody['filters']> = {};
  if (f.language) out.language = f.language;
  if (f.lastPlayedWithinDays != null && f.lastPlayedWithinDays > 0) out.lastPlayedWithinDays = f.lastPlayedWithinDays;
  return Object.keys(out).length ? out : undefined;
}

/** GET …/mail/audience の問い合わせ文字列（'?language=ja&lastPlayedWithinDays=30' / 絞り込みなしは ''） */
export function audienceQuery(f: Pick<ComposeForm, 'language' | 'lastPlayedWithinDays'>): string {
  const filters = audienceFilters(f);
  if (!filters) return '';
  const q = new URLSearchParams();
  if (filters.language) q.set('language', filters.language);
  if (filters.lastPlayedWithinDays) q.set('lastPlayedWithinDays', String(filters.lastPlayedWithinDays));
  return `?${q.toString()}`;
}

/**
 * 「今すぐ送る」を押せない理由（送れる状態 = mailBlocks が空、を満たしたうえで）。
 * 宛先が 0 人 = 送っても誰にも届かないので押させない。予約・定期は止めない（その時刻までに同意する人が増えうる）。
 * 宛先の数が分からない（null・読み込み中 / 失敗）は止めない: 数え直しの失敗で送れなくなるのは困る。確認ダイアログに「不明」と出す
 */
export function nowBlocked(mode: SendMode, recipients: number | null): boolean {
  return mode === 'now' && recipients === 0;
}

// ---------------------------------------------------------------- 履歴

export type BroadcastStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'canceled' | 'failed';

/**
 * 取り消せるか: 下書き・予約（定期は次回待ちの間も scheduled）・送信中（残りを止める）。送り終えたものは取り消せない（届いている）。
 * 下書きも取り消せる（Core の cancelBroadcast が draft を受ける）= 使わない下書きを履歴の「下書き」から片付けられる
 */
export function canCancel(status: string): boolean {
  return status === 'draft' || status === 'scheduled' || status === 'sending';
}

/** 下書き = 作ったが送っていない（「今すぐ送る」の作成は済んで送信で失敗した、など）。履歴から送り直せる */
export function canSendDraft(status: string): boolean {
  return status === 'draft';
}

/**
 * 送れなかった理由の文言の分かれ目（Core の統合⑰）。返すのは mailDisplay.ts の FAILURE_KEYS の名前。
 * WHY balance_due を定期だけ分けるか: 2026-09-27 core/r3-teardown から、残高切れの定期は親ごと取り消される
 *   （broadcasts.ts の STOPS_RECURRENCE）= **入金しても古い定期は再開しない**。1 回の配信の文言「残高を足してもう一度送ってください」
 *   のままだと、入金すれば定期が戻ると読めてしまうので、定期（親 = recurrence あり・回の行 = parentId あり）は「作り直してください」にする
 */
export function failureVariant(reason: string, b: { recurrence?: unknown; parentId?: string | null }): string {
  if (reason === 'balance_due' && (b.recurrence || b.parentId)) return 'balance_due_recurring';
  return reason;
}

export function statusTone(status: string): 'ok' | 'danger' | 'accent' | 'warn' | 'none' {
  switch (status) {
    case 'sent':
      return 'ok';
    case 'failed':
      return 'danger';
    case 'sending':
    case 'scheduled':
      return 'accent';
    case 'draft':
      return 'warn';
    default:
      return 'none';
  }
}

/**
 * 自動停止の線（案 D11）: 苦情 0.1% 以上 または ハードバウンス 5% 以上（直近 N 通。N は既定 500）。
 * 画面では 1 回の配信の率を並べ、線の半分を超えたら warn・線以上で danger にする
 * （止まる前に「宛先のリストが古い・内容が迷惑がられている」に気づいてもらう。止まってからでは解除に運営の確認が要る）。
 * 1 回の配信の率は直近 N 通の判定と同じではない = 目安として出す（文言でもそう書く）
 */
export const STOP_LINES = { complaint: 0.001, bounce: 0.05 } as const;

export function rateOf(part: number | null | undefined, whole: number | null | undefined): number | null {
  if (part == null || whole == null || whole <= 0) return null;
  return part / whole;
}

export function rateTone(rate: number | null, line: number): 'danger' | 'warn' | 'none' {
  if (rate == null) return 'none';
  if (rate >= line) return 'danger';
  if (rate >= line / 2) return 'warn';
  return 'none';
}
